const MARK = "<!--CRM_BOARD-->";

export type BoardColumn = { id: string; title: string };
export type BoardWatch = { id: number; mail: boolean; sms: boolean };
export type CardNotice = { names: string[]; gaps: string[]; at: string };
export type BoardCard = { id: string; columnId: string; title: string; note: string; props: Record<string, string>; page: string; notice: CardNotice | null };
export type BoardValue = { key: string; label: string; color: string };
export type BoardProperty = { key: string; label: string; values: BoardValue[] };
export type BoardLayout = "columns" | "grid";
export type BoardFile = {
  background: string;
  subtitle: string;
  layout: BoardLayout;
  columns: BoardColumn[];
  cards: BoardCard[];
  source: "main" | "local";
  mainAll: boolean;
  mainKeys: string[];
  properties: BoardProperty[];
  notify: BoardWatch[];
};

export const BOARD_BACKGROUNDS = ["#f8fafc", "#e0f2fe", "#dcfce7", "#fef9c3", "#ffedd5", "#fce7f3", "#ede9fe", "#e2e8f0"];
export const BOARD_SWATCHES = ["#64748b", "#2563eb", "#0f766e", "#d97706", "#e11d48", "#7c3aed", "#c2410c", "#1d4ed8"];

export function isBoardName(name: string): boolean {
  return name.toLowerCase().endsWith(".board.md");
}

export function isPipeBoard(name: string): boolean {
  return name.toLowerCase() === "pipeline.board.md";
}

/** Le suivi interne est un tableau de cartes. Les propriétés et les notifications vivent sur un kanban de fiche. */
export function plainSuivi(board: BoardFile): BoardFile {
  return {
    ...board,
    source: "local",
    mainAll: false,
    mainKeys: [],
    properties: [],
    notify: [],
    cards: board.cards.map((card) => ({ ...card, props: {}, notice: null })),
  };
}

export function suiviDirty(board: BoardFile): boolean {
  if (board.notify.length > 0 || board.properties.length > 0 || board.source !== "local" || board.mainAll || board.mainKeys.length > 0) return true;
  return board.cards.some((card) => card.notice !== null || Object.keys(card.props).some((key) => card.props[key] !== ""));
}

export function itemTitle(name: string): string {
  if (isBoardName(name)) return name.replace(/\.board\.md$/i, "");
  return name.replace(/\.md$/i, "");
}

export function emptyBoard(): BoardFile {
  return {
    background: BOARD_BACKGROUNDS[0],
    subtitle: "",
    layout: "columns",
    columns: [{ id: freshId("col"), title: "À faire" }],
    cards: [],
    source: "main",
    mainAll: true,
    mainKeys: [],
    properties: [],
    notify: [],
  };
}

export function blankCard(columnId: string, title: string): BoardCard {
  return { id: freshId("card"), columnId, title, note: "", props: {}, page: "", notice: null };
}

export function parseBoard(raw: string): BoardFile {
  const at = raw.indexOf(MARK);
  const json = (at >= 0 ? raw.slice(at + MARK.length) : raw).trim();
  const data = JSON.parse(json) as Partial<BoardFile>;
  const columns = Array.isArray(data.columns)
    ? data.columns
        .filter((column) => column && typeof column.id === "string" && column.id !== "")
        .map((column) => ({ id: column.id, title: String(column.title || "Colonne") }))
    : [];
  const known = new Set(columns.map((column) => column.id));
  const cards = Array.isArray(data.cards)
    ? data.cards
        .filter((card) => card && typeof card.id === "string" && known.has(String(card.columnId)))
        .map((card) => ({
          id: card.id,
          columnId: String(card.columnId),
          title: String(card.title || ""),
          note: String(card.note || ""),
          props: card.props && typeof card.props === "object" ? Object.fromEntries(Object.entries(card.props).map(([key, value]) => [key, String(value)])) : {},
          page: String(card.page || ""),
          notice: readNotice(card.notice),
        }))
    : [];
  const properties = Array.isArray(data.properties) ? data.properties.flatMap(readProperty) : [];
  const base = emptyBoard();
  return {
    background: typeof data.background === "string" && data.background ? data.background : base.background,
    subtitle: typeof data.subtitle === "string" ? data.subtitle : "",
    layout: data.layout === "grid" ? "grid" : "columns",
    columns: columns.length > 0 ? columns : base.columns,
    cards,
    source: data.source === "local" ? "local" : "main",
    mainAll: data.mainAll === false ? false : data.mainAll === true ? true : !Array.isArray(data.mainKeys),
    mainKeys: Array.isArray(data.mainKeys) ? data.mainKeys.filter((key) => typeof key === "string" && key !== "") : [],
    properties,
    notify: readWatchList(data.notify),
  };
}

function readWatchList(raw: unknown): BoardWatch[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<number>();
  const rows: BoardWatch[] = [];
  raw.forEach((item) => {
    if (!item || typeof item !== "object") return;
    const id = Number((item as { id?: unknown }).id) || 0;
    const mail = (item as { mail?: unknown }).mail === true;
    const sms = (item as { sms?: unknown }).sms === true;
    if (id <= 0 || seen.has(id) || (!mail && !sms)) return;
    seen.add(id);
    rows.push({ id, mail, sms });
  });
  return rows;
}

function readNotice(raw: unknown): CardNotice | null {
  if (!raw || typeof raw !== "object") return null;
  const names = stringList((raw as { names?: unknown }).names);
  const gaps = stringList((raw as { gaps?: unknown }).gaps);
  if (names.length === 0 && gaps.length === 0) return null;
  const at = String((raw as { at?: unknown }).at || "");
  return { names, gaps, at };
}

function stringList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => String(item || "").trim()).filter(Boolean);
}

export function watchActive(rows: BoardWatch[]): boolean {
  return rows.some((row) => row.mail || row.sms);
}

export function columnChanged(before: BoardFile, after: BoardFile): BoardCard | null {
  for (const card of after.cards) {
    const previous = before.cards.find((item) => item.id === card.id);
    if (previous && previous.columnId !== card.columnId) return card;
  }
  return null;
}

export function writeNotice(board: BoardFile, cardId: string, notice: CardNotice): BoardFile {
  return {
    ...board,
    cards: board.cards.map((card) => (card.id === cardId ? { ...card, notice } : card)),
  };
}

function readProperty(property: BoardProperty): BoardProperty[] {
  if (!property || typeof property !== "object") return [];
  const label = typeof property.label === "string" ? property.label.trim() : "";
  if (!label) return [];
  const values = Array.isArray(property.values)
    ? property.values.flatMap((value) => {
        if (!value || typeof value !== "object") return [];
        const name = typeof value.label === "string" ? value.label.trim() : "";
        if (!name) return [];
        return [{ key: typeof value.key === "string" && value.key ? value.key : freshId("val"), label: name, color: typeof value.color === "string" && value.color ? value.color : BOARD_SWATCHES[0] }];
      })
    : [];
  return [{ key: typeof property.key === "string" && property.key ? property.key : freshId("prop"), label, values }];
}

export function renderBoard(board: BoardFile): string {
  return `${MARK}\n${JSON.stringify(board)}\n`;
}

let boardWrites: Promise<void> = Promise.resolve();

/** Une seule file pour le kanban ouvert et le suivi : le dernier geste gagne. */
export function enqueueBoardWrite(task: () => Promise<void>): Promise<void> {
  const job = boardWrites.then(task, task);
  boardWrites = job.then(
    () => undefined,
    () => undefined,
  );
  return job;
}

export function flushBoardWrites(): Promise<void> {
  return enqueueBoardWrite(async () => undefined);
}

export function freshId(prefix: string): string {
  return `${prefix}_${Math.random().toString(16).slice(2, 10)}`;
}

export function todayStamp(date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}
